// src/services/todoService.js
//
// ToDo API calls (My Page / Inbox architecture)
// ============================================
//
// This service is intentionally SMALL now.
//
// Why?
// -----
// Your app has moved to the "My Page" architecture where:
//
// - HTTP is the single source of truth for the Dashboard inbox:
//     GET /my-page   -> returns the authoritative work items snapshot
//
// - Realtime (Socket.IO) is only used for invalidation notifications:
//     backend emits "myPageInvalidated" to affected users
//     frontend refetches GET /my-page (debounced)
//
// Therefore, this file now contains ONLY the minimal ToDo mutations
// that still exist as ToDo-specific endpoints:
//
//   POST /todos          -> create a ToDo (work item)
//   PUT  /todos/:id/done -> mark ToDo as done (close work item)
//
// What was removed (by design)
// ----------------------------
// ✅ fetchTodos      -> replaced by fetchMyPage() in Dashboard
// ✅ addComment      -> replaced by work-item universal chat endpoints (WorkItemChatPage)
// ✅ fetchComments   -> replaced by work-item universal chat endpoints (WorkItemChatPage)
//
// IMPORTANT UI NOTE
// -----------------
// Backend returns useful validation messages on 4xx, e.g.:
//   { message: "Assignee does not exist or is inactive." } (HTTP 400)
//
// We KEEP throwing the raw axios error (good), and export a helper
// getApiErrorMessage() so pages can consistently display backend messages.
//
// Conventions used in this project
// -------------------------------
// - Use apiClient (Pattern A: refresh handled centrally via apiClient interceptors).
// - Use try/catch + contextual console.error + rethrow.
// - Pass withCredentials:true explicitly for consistency and to avoid regressions.
// - Accept AbortSignal even for mutations (optional, but safe and consistent).
// - Validate IDs defensively (assertValidId) so we fail fast client-side.
//
// NOTE
// ----
// In this file we do NOT deal with sockets at all.
// Controllers/services on backend emit invalidation events after DB commits.
// Pages (Dashboard) decide whether to refetch /my-page after local actions.

import apiClient from "../utils/apiClient";
import { isRequestCanceled, assertValidId } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Error helper (optional, but very useful)                                   */
/* -------------------------------------------------------------------------- */

/**
 * Extract a user-friendly message from an axios error.
 *
 * Why this exists:
 * - Many backend endpoints return { message: "..."} on 4xx validation failures.
 * - UI should show these messages instead of a generic "Please try again."
 *
 * Safe behavior:
 * - Prefer server-provided message (message/error).
 * - Fall back to axios error.message
 * - Fall back to provided default
 *
 * This helper does NOT log — callers decide whether to log.
 *
 * @param {any} err - axios error object (or any thrown value)
 * @param {string} fallback - default message if nothing better exists
 * @returns {string} user-friendly message
 */
export function getApiErrorMessage(err, fallback = "Request failed.") {
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message;

  const clean = typeof msg === "string" ? msg.trim() : "";
  return clean || fallback;
}

/* -------------------------------------------------------------------------- */
/* POST /todos                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Create a new ToDo (work item).
 *
 * Endpoint:
 *   POST /todos
 *
 * Expected payload example:
 *   {
 *     description: "Call client",
 *     is_private: false,
 *     assignee_id: 123
 *   }
 *
 * Notes:
 * - Backend performs real validation (assignee exists + active, etc.)
 * - Backend emits realtime invalidation AFTER commit:
 *     notifyMyPageInvalidated(recipients, { reason: "TODO_CREATED", workItemKey: "todo:<id>" })
 * - Dashboard may additionally refetch /my-page locally for immediate confirmation.
 *
 * @param {object} todoData
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - optional cancellation signal
 * @returns {Promise<object>} created ToDo row
 */
export const createTodo = async (todoData, options = {}) => {
  try {
    const response = await apiClient.post("/todos", todoData, {
      withCredentials: true,
      signal: options.signal,
    });

    return response.data;
  } catch (error) {
    // Cancellation is expected during navigation/unmount; do not treat as failure.
    if (isRequestCanceled(error)) throw error;

    console.error("[TodoService] ❌ createTodo failed:", error);
    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/* PUT /todos/:id/done                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Mark a ToDo as done.
 *
 * Endpoint:
 *   PUT /todos/:id/done
 *
 * Notes:
 * - Backend should enforce authorization (creator/assignee/superuser).
 * - Backend emits realtime invalidation AFTER commit:
 *     notifyMyPageInvalidated(recipients, { reason: "TODO_DONE", workItemKey: "todo:<id>" })
 *
 * @param {number|string} todoId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} backend response payload
 */
export const markTodoDone = async (todoId, options = {}) => {
  try {
    assertValidId(todoId, "todoId");

    const response = await apiClient.put(
      `/todos/${todoId}/done`,
      {}, // keep explicit body to avoid axios quirks; backend ignores it
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return response.data;
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error(`[TodoService] ❌ markTodoDone(${todoId}) failed:`, error);
    throw error;
  }
};
